// VideosSection.tsx — onglet « Vidéos » du Dashboard : les dernières vidéos déposées
//
// Refait le 2026-10-17 d'après la maquette validée par l'utilisateur
// (https://claude.ai/artifact/MtJ8i9W8i9G2uHH3CcnL7N), sur le modèle de l'onglet
// Chaînes : chiffres du haut, filtres État / Univers, la plus récente en grand, puis
// les cartes par jour ; vue liste en bascule. L'état de transcodage se lit sur la
// vignette (voile, pourcentage, barre ambre) pour repérer d'un coup d'œil ce qui
// coince. Données : admin-api DashboardLatest (`videos` et `videoStats`).

import VideoFeatureButton from './VideoFeatureButton';
import React, { useEffect, useMemo, useState } from 'react';

import styles from './ChannelsSection.module.scss';
import tabs   from './DashboardTabs.module.scss';
import extra  from './VideosSection.module.scss';
import RecentCommentsKpi from './RecentCommentsKpi';
import CommentsPanel     from './CommentsPanel';
import { Geo } from './ActivityOverview';
import { ListTable, TitleCell, CreatorCell, WhenCell, RowActions, Dash } from './DashboardList';
import { WWW_URLS } from '../../../backendServices/urls';
import { getAdminEventContext, AdminEventContext } from '../../../backendServices/adminManagment/api_adminEvents';
import { Owner, Segment, usePicked, ViewToggle, EmptyState, Spark, useView, groupByDay, ago, hour, clock,
         plural, tintOf, shortDay, size, creatorPlace, CREATOR_PLACE_HINT } from './dashboardShared';

export type VideoState  = 'transcoding' | 'ready' | 'hidden' | 'failed';
export type VideoOrigin = 'regie' | 'ios' | 'android';

export type VideoKpi = 'today' | 'week' | 'working' | 'visible' | null;

export interface DashboardVideo {
  id              : number;
  ownerId?        : number;
  /** Fin de la mise à la une du portail (date ISO) ; null ou absent : pas à la une. */
  featuredUntil?  : string | null;
  name            : string;
  publicRef       : string;
  hasThumbnail    : boolean;
  creationDate    : string | null;
  owner           : string;
  ownerEmail      : string;
  state           : VideoState;
  progress        : number | null;
  durationSeconds : number | null;
  active          : boolean;
  publicPortal    : boolean;
  views           : number;
  resolutions     : string;
  social          : string[];
  universe        : string | null;
  origin          : VideoOrigin;
  sizeBytes       : number;
  geo?            : Geo | null;
}

export interface VideoStats {
  today             : number;
  yesterday         : number;
  week              : number;
  days              : { day: string; count: number }[];
  states            : Record<VideoState, number>;
  oldestTranscoding : string | null;
}

interface Props {
  videos   : DashboardVideo[];
  stats    : VideoStats | null;
  loading  : boolean;
  error    : boolean;
  filtered : boolean;
  onClearFilters : () => void;
  onPlay   : (v: DashboardVideo) => void;
  onSheet  : (v: DashboardVideo) => void;
  onRetry  : (v: DashboardVideo) => Promise<boolean>;
  /** Désactive (et retire du portail) ou rétablit ; renvoie un message d'erreur, '' si réussi. */
  onAvailability : (v: DashboardVideo, enabled: boolean, portal: boolean, comment: string) => Promise<{ error: string; notice: string }>;
  adminId  : number | undefined;
  /** Désactiver / rétablir une vidéo : réservé au superadmin (faux pour un créateur). */
  canModerate? : boolean;
  /** Mise à la une changée : recharger la liste. */
  onFeatureChanged? : () => void;
  /** Graphe, carte et villes de la période (ActivityOverview), placés sous les chiffres. */
  overview?: React.ReactNode;
  /** Éléments du point désigné sur la carte : surlignés, le premier amené à l'écran. */
  picked?: DashboardVideo[];
  kpi?   : VideoKpi;
  onKpi? : (kpi: VideoKpi) => void;
}

const VIEW_KEY = 'dashboardV2.videosView';

export type Sort = 'recent' | 'views' | 'duration' | 'size' | 'name';
export const SORTS: readonly (readonly [Sort, string])[] = [
  [ 'recent', 'Plus récentes' ], [ 'views', 'Plus vues' ], [ 'duration', 'Plus longues' ], [ 'size', 'Plus lourdes' ], [ 'name', 'Nom (A → Z)' ],
];
const SORT_KEY = 'dashboardV2.videosSort';
const byDateDesc = (a: DashboardVideo, b: DashboardVideo) => (b.creationDate ?? '').localeCompare(a.creationDate ?? '');
export const COMPARE: Record<Sort, (a: DashboardVideo, b: DashboardVideo) => number> = {
  recent  : byDateDesc,
  views   : (a, b) => b.views - a.views || byDateDesc(a, b),
  duration: (a, b) => (b.durationSeconds ?? -1) - (a.durationSeconds ?? -1) || byDateDesc(a, b),
  size    : (a, b) => b.sizeBytes - a.sizeBytes || byDateDesc(a, b),
  name    : (a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base', numeric: true }),
};
const NEW_MS   = 3600_000;
export const ORIGIN_LABEL: Record<VideoOrigin, string> = { regie: 'Régie web', ios: 'Kleon Studio iPhone', android: 'Kleon Studio Android' };
const SOCIAL_LABEL: Record<string, string> = { youtube: 'YouTube', facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok', twitch: 'Twitch', linkedin: 'LinkedIn', x: 'X', twitter: 'X' };

export const isNew = (v: DashboardVideo) => !!v.creationDate && Date.now() - new Date(v.creationDate).getTime() < NEW_MS;

/** « 1080,720 », « ["1080p","720p"] »… → « 1080p · 720p ». */
export const resolutionsText = (raw: string) => {
  const list = Array.from(new Set((raw.match(/\d{3,4}/g) ?? []).map(Number).filter(n => n >= 144))).sort((a, b) => b - a);
  return list.map(n => `${n}p`).join(' · ');
};

export const socialText = (code: string) => SOCIAL_LABEL[code.toLowerCase()] ?? (code.charAt(0).toUpperCase() + code.slice(1));

const progressText = (v: DashboardVideo) =>
  v.progress == null ? 'En attente du transcodeur' : v.progress >= 100 ? 'Finalisation…' : `Transcodage ${Math.floor(v.progress)} %`;

// ── Petits morceaux ───────────────────────────────────────────────────────────

export const StateBadge: React.FC<{ v: DashboardVideo }> = ({ v }) => {
  switch (v.state) {
    case 'transcoding': return <span className={`${styles.badge} ${tabs.badgeAmber}`}><span className={tabs.dotAmber}/>{v.progress == null ? 'EN ATTENTE' : `TRANSCODAGE ${Math.floor(v.progress)} %`}</span>;
    case 'failed'     : return <span className={`${styles.badge} ${tabs.badgeRed}`}>ÉCHEC</span>;
    case 'hidden'     : return <span className={`${styles.badge} ${styles.badgeOff}`}>MASQUÉE</span>;
    default           : return v.publicPortal
                          ? <span className={`${styles.badge} ${styles.badgeOn}`}>SUR KLEON.TV</span>
                          : <span className={`${styles.badge} ${tabs.badgeBlue}`}>EN LIGNE</span>;
  }
};

export const Thumb: React.FC<{ v: DashboardVideo; big?: boolean; onPlay?: (v: DashboardVideo) => void }> = ({ v, big, onPlay }) => {
  const [ failed, setFailed ] = useState(false);
  const src = v.hasThumbnail && v.publicRef ? `${WWW_URLS.MediaServer}${v.publicRef}/images/thumbnail.webp` : '';
  const veiled = v.state !== 'ready';
  const canPlay = !!onPlay && !!v.publicRef && (v.state === 'ready' || v.state === 'hidden');
  const play = canPlay ? { role: 'button', tabIndex: 0, 'aria-label': `Lire ${v.name}`, onClick: () => onPlay!(v),
                           onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPlay!(v); } } } : {};
  return (
    <div className={`${styles.thumb} ${veiled ? tabs.veil : ''} ${canPlay ? extra.playable : ''}`} style={{ background: tintOf(v.id) }} {...play}>
      {canPlay && <span className={extra.playHint} aria-hidden="true">▶</span>}
      {src && !failed
        ? <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
        : <span className={big ? styles.thumbNameBig : styles.thumbName}>{v.name}</span>}
      {/* position forcée : `td .badges` la rend statique quand les cartes sont dans une cellule (fiche créateur, Ressources) */}
      <span className={styles.badges} style={{ zIndex: 1, position: 'absolute' }}>
        {isNew(v) && <span className={`${styles.badge} ${styles.badgeNew}`}>NOUVELLE</span>}
        <StateBadge v={v} />
      </span>
      {v.state !== 'failed' && v.durationSeconds != null && <span className={tabs.corner}>{clock(v.durationSeconds)}</span>}
      {v.state === 'transcoding' && v.progress != null && <div className={tabs.progress}><span style={{ width: `${Math.min(100, v.progress)}%` }}/></div>}
    </div>
  );
};

/** Vignette de la vue liste : l'image de la vidéo, voilée tant qu'elle n'est pas visible. */
export const MiniThumb: React.FC<{ v: DashboardVideo }> = ({ v }) => {
  const [ failed, setFailed ] = useState(false);
  const src = v.hasThumbnail && v.publicRef ? `${WWW_URLS.MediaServer}${v.publicRef}/images/thumbnail.webp` : '';
  return (
    <div className={v.state !== 'ready' ? tabs.veil : undefined} style={{ width: '100%', height: '100%', background: tintOf(v.id) }}>
      {src && !failed && <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />}
    </div>
  );
};

export const Actions: React.FC<{ v: DashboardVideo; onPlay: Props['onPlay']; onSheet: Props['onSheet']; onRetry: Props['onRetry']; onToggle?: (v: DashboardVideo) => void; feature?: { adminId: number; onChanged: () => void }; big?: boolean; playIcon?: boolean }> =
  ({ v, onPlay, onSheet, onRetry, onToggle, feature, big, playIcon }) => {
  const [ retrying, setRetrying ] = useState(false);
  const retry = async () => { setRetrying(true); await onRetry(v); setRetrying(false); };
  return (
    <div className={big ? styles.actionsBig : styles.actions}>
      {v.state === 'failed'
        ? <button type="button" className={styles.primary} disabled={retrying} onClick={retry}>{retrying ? 'Relance…' : 'Relancer le transcodage'}</button>
        : <button type="button" className={styles.primary} disabled={v.state === 'transcoding' || !v.publicRef}
                  title={v.state === 'transcoding' ? 'Lisible à la fin du transcodage' : undefined}
                  aria-label={playIcon ? `Lire ${v.name}` : undefined}
                  onClick={() => onPlay(v)}>{big ? 'Regarder' : playIcon
                    ? <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
                    : 'Lire'}</button>}
      <button type="button" className={styles.secondary} onClick={() => onSheet(v)}>{big ? 'Ouvrir la fiche' : 'Fiche'}</button>
      {onToggle && (v.state === 'ready' || v.state === 'hidden') &&
        <button type="button" className={`${styles.secondary} ${v.state === 'hidden' ? extra.restore : extra.disable}`} onClick={() => onToggle(v)}
                title={v.state === 'hidden' ? 'Rétablir la vidéo' : 'Désactiver la vidéo et la retirer du portail Kleon.tv'}>
          {v.state === 'hidden' ? 'Rétablir' : 'Désactiver'}
        </button>}
      {feature && v.state === 'ready' && <VideoFeatureButton v={v} adminId={feature.adminId} onChanged={feature.onChanged} />}
    </div>
  );
};

export const Meta: React.FC<{ v: DashboardVideo }> = ({ v }) => (
  <div className={tabs.cardMeta}>
    <span>{hour(v.creationDate)}</span>
    {v.state === 'transcoding' && <span className={tabs.amber}>{progressText(v)}</span>}
    {v.state === 'failed'      && <span className={tabs.red}>Transcodage en échec</span>}
    {(v.state === 'ready' || v.state === 'hidden') && <span>{plural(v.views, 'vue')}</span>}
    {resolutionsText(v.resolutions) && <span>{resolutionsText(v.resolutions)}</span>}
    {size(v.sizeBytes) && <span>{size(v.sizeBytes)}</span>}
  </div>
);

const PORTAL_KEY = 'dashboardV2.portalBeforeDisable';
const readPortal = (id: number): boolean => { try { return JSON.parse(localStorage.getItem(PORTAL_KEY) ?? '{}')[id] === true; } catch { return false; } };
const savePortal = (id: number, on: boolean | null) => {
  try { const all = JSON.parse(localStorage.getItem(PORTAL_KEY) ?? '{}'); if (on === null) delete all[id]; else all[id] = on; localStorage.setItem(PORTAL_KEY, JSON.stringify(all)); } catch { /* sans stockage : case décochée */ }
};

/** Confirmation avant de désactiver (et retirer du portail) ou de rétablir une vidéo. */
export const AvailabilityDialog: React.FC<{ video: DashboardVideo; adminId: number | undefined; onClose: () => void; onConfirm: Props['onAvailability'] }> = ({ video: v, adminId, onClose, onConfirm }) => {
  const restoring = v.state === 'hidden';
  // Au rétablissement, la case reprend l'état du portail avant la désactivation (mémorisé dans ce navigateur).
  const [ portal , setPortal  ] = useState(() => restoring && readPortal(v.id));
  const [ busy   , setBusy    ] = useState(false);
  const [ error  , setError   ] = useState('');
  const [ ctx    , setCtx     ] = useState<AdminEventContext | null>(null);
  const [ ctxErr , setCtxErr  ] = useState('');
  const [ comment, setComment ] = useState('');
  const [ done   , setDone    ] = useState<string | null>(null);   // message de fin, la fenêtre reste ouverte pour qu'on le lise

  // Ce que le serveur inscrira dans le dossier du créateur : compte, IP, date et heure (lecture seule).
  useEffect(() => {
    if (!adminId) { setCtxErr('Compte non identifié : reconnectez-vous.'); return; }
    let cancelled = false;
    getAdminEventContext(adminId).then(r => { if (cancelled) return; if (r.ok) setCtx(r.data); else setCtxErr(r.error); });
    return () => { cancelled = true; };
  }, [adminId]);

  const needsEvent = ctx?.superadmin === true;
  const ready      = (ctx !== null || ctxErr !== '') && (!needsEvent || comment.trim().length > 0);

  const confirm = async () => {
    setBusy(true); setError('');
    if (!restoring) savePortal(v.id, v.publicPortal);
    const out = await onConfirm(v, restoring, portal, comment.trim());
    setBusy(false);
    if (out.error) { setError(out.error); return; }
    if (restoring) savePortal(v.id, null);
    if (needsEvent) setDone(`${restoring ? 'Vidéo rétablie' : 'Vidéo désactivée'}. Événement ajouté à la fiche du créateur.${out.notice ? ' ' + out.notice : ''}`);
    else onClose();
  };

  return (
    <div className={extra.overlay} onClick={() => !busy && onClose()}>
      <div className={extra.dialog} role="dialog" aria-modal="true" aria-label={restoring ? 'Rétablir la vidéo' : 'Désactiver la vidéo'} onClick={e => e.stopPropagation()}>
        <h3>{restoring ? 'Rétablir cette vidéo ?' : 'Désactiver cette vidéo ?'}</h3>
        <p className={extra.dialogName}>« {v.name} »</p>
        {done ? (
          <>
            <p role="status">{done}</p>
            <div className={extra.dialogActions}><button type="button" className={styles.primary} onClick={onClose} autoFocus>Fermer</button></div>
          </>
        ) : <>
        {restoring ? (
          <>
            <p>La vidéo redevient diffusable. Rien n'avait été supprimé.</p>
            <label className={extra.check}>
              <input type="checkbox" checked={portal} onChange={e => setPortal(e.target.checked)} />
              La remettre aussi sur le portail Kleon.tv
            </label>
          </>
        ) : (
          <p>La vidéo ne sera plus diffusée{v.publicPortal ? ' et sera retirée du portail Kleon.tv' : ''}. Elle n'est pas supprimée : vous pourrez la rétablir à tout moment.</p>
        )}
        {needsEvent && ctx && (
          <fieldset className={extra.event}>
            <legend>Événement inscrit sur la fiche du créateur ({v.owner || v.ownerEmail || 'inconnu'})</legend>
            <label>À l'origine<input className={extra.readonly} value={ctx.name} readOnly /></label>
            <div className={extra.two}>
              <label>Adresse IP<input className={`${extra.readonly} ${extra.mono}`} value={ctx.ip ?? 'inconnue'} readOnly /></label>
              <label>Date et heure<input className={extra.readonly} value={new Date(ctx.at).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })} readOnly /></label>
            </div>
            <label>Commentaire <strong>(obligatoire)</strong>
              <textarea className={extra.textarea} rows={4} maxLength={5000} value={comment} autoFocus aria-invalid={comment.trim() === ''}
                        placeholder={restoring ? 'Pourquoi rétablir cette vidéo ? (contrôle effectué, accord du créateur…)' : 'Pourquoi désactiver cette vidéo ? (motif, signalement, demande du créateur…)'}
                        onChange={e => setComment(e.target.value)} />
            </label>
            <span className={extra.note}>L'heure est celle du serveur à l'envoi. L'événement apparaît dans l'onglet Événements du créateur ; le créateur est aussi prévenu par e-mail (sans ce commentaire).</span>
          </fieldset>
        )}
        {!ctx && !ctxErr && <p className={extra.note}>Chargement…</p>}
        {ctxErr && <p className={extra.dialogError} role="alert">{ctxErr}</p>}
        {error && <p className={extra.dialogError} role="alert">{error}</p>}
        <div className={extra.dialogActions}>
          <button type="button" className={styles.secondary} disabled={busy} onClick={onClose}>Annuler</button>
          <button type="button" className={restoring ? styles.primary : extra.confirmDanger} disabled={busy || !ready} onClick={confirm} autoFocus={!needsEvent}>
            {busy ? '…' : restoring ? 'Rétablir' : 'Désactiver'}
          </button>
        </div>
        </>}
      </div>
    </div>
  );
};

// ── Composant ────────────────────────────────────────────────────────────────

const VideosSection: React.FC<Props> = ({ onFeatureChanged, kpi, onKpi, videos, stats, loading, error, filtered, onClearFilters, onPlay, onSheet, onRetry, onAvailability, adminId, overview, picked, canModerate = true }) => {
  const { ref: pickRef, mark } = usePicked(picked, (v: DashboardVideo) => v.id);
  const [ state,    setState    ] = useState<'all' | VideoState>('all');
  const [ universe, setUniverse ] = useState<string>('all');
  const [ query,    setQuery    ] = useState('');
  const [ sort,     setSortRaw  ] = useState<Sort>(() => {
    try { const v = localStorage.getItem(SORT_KEY) as Sort | null; return v && v in COMPARE ? v : 'recent'; } catch { return 'recent'; }
  });
  const setSort = (v: Sort) => { setSortRaw(v); try { localStorage.setItem(SORT_KEY, v); } catch { /* sans stockage : tri valable pour la session */ } };
  const [ view, setView ] = useView(VIEW_KEY);
  const [ commentsOpen, setCommentsOpen ] = useState(false);
  const [ toggling, setToggling ] = useState<DashboardVideo | null>(null);

  const askToggle = (v: DashboardVideo) => setToggling(v);

  // Les univers présents dans la liste, les plus fréquents d'abord (5 au plus).
  const universes = useMemo(() => {
    const count = new Map<string, number>();
    videos.forEach(v => { if (v.universe) count.set(v.universe, (count.get(v.universe) ?? 0) + 1); });
    return Array.from(count.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([u]) => u);
  }, [videos]);

  // Recherche par titre, créateur ou univers ; les compteurs d'état tiennent compte de la recherche et de l'univers.
  const matching = useMemo(() => {
    const q = query.trim().toLowerCase();
    return videos.filter(v =>
      (universe === 'all' || v.universe === universe) &&
      (!q || `${v.name} ${v.owner} ${v.ownerEmail} ${v.universe ?? ''}`.toLowerCase().includes(q)));
  }, [videos, universe, query]);

  const shown = useMemo(
    () => matching.filter(v => state === 'all' || v.state === state).sort(COMPARE[sort]),
    [matching, state, sort]);

  const counts = useMemo(() => {
    const c: Record<'all' | VideoState, number> = { all: matching.length, transcoding: 0, ready: 0, hidden: 0, failed: 0 };
    matching.forEach(v => { c[v.state]++; });
    return c;
  }, [matching]);

  const localFilters = query.trim() !== '' || state !== 'all' || universe !== 'all';
  const resetLocal   = () => { setQuery(''); setState('all'); setUniverse('all'); };

  // Par date : les cartes par jour, toutes de la même taille (la plus récente n'est plus mise en grand). Autre tri : une seule grille, dans l'ordre choisi.
  const byDate = sort === 'recent';
  const groups = useMemo(
    () => byDate ? groupByDay(shown, v => v.creationDate) : [{ title: SORTS.find(([k]) => k === sort)![1], items: shown }],
    [shown, byDate, sort]);
  const delta  = stats ? stats.today - stats.yesterday : 0;
  const st     = stats?.states;
  const total  = st ? Math.max(1, st.ready + st.hidden + st.transcoding + st.failed) : 1;
  const working = st ? st.transcoding : videos.filter(v => v.state === 'transcoding').length;

  return (
    <div className={styles.root} ref={pickRef}>

      {/* ── Chiffres ── */}
      <div className={`${styles.kpis} ${styles.kpis5}`}>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${kpi === 'today' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'today'}
                title="Cliquer pour ne montrer que ces vidéos (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'today' ? null : 'today')}>
          <span className={styles.kpiLabel}>Ajoutées aujourd'hui</span>
          <span className={styles.kpiValue}>{stats?.today ?? '–'}</span>
          {stats && <span className={delta >= 0 ? styles.kpiUp : styles.kpiMuted}>
            {delta === 0 ? 'autant qu’hier' : `${delta > 0 ? '+' : ''}${delta} par rapport à hier`}</span>}
        </button>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${kpi === 'week' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'week'}
                title="Cliquer pour ne montrer que les vidéos ajoutées sur 7 jours (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'week' ? null : 'week')}>
          <span className={styles.kpiLabel}>Sur 7 jours</span>
          <span className={styles.kpiValue}>{stats?.week ?? '–'}</span>
          {stats && <Spark values={stats.days.map(d => ({ key: d.day, value: d.count, title: `${shortDay(d.day)} : ${d.count}` }))} />}
        </button>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${working > 0 ? tabs.kpiWarn : ''} ${kpi === 'working' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'working'}
                title="Cliquer pour ne montrer que les vidéos en transcodage (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'working' ? null : 'working')}>
          <span className={styles.kpiLabel}>En transcodage</span>
          <span className={`${styles.kpiValue} ${working > 0 ? tabs.amber : ''}`}>{working}</span>
          <span className={styles.kpiMuted}>
            {stats?.oldestTranscoding ? `la plus ancienne ${ago(stats.oldestTranscoding)}` : 'aucune en attente'}
            {st && st.failed > 0 && <> · <span className={tabs.red}>{plural(st.failed, 'en échec', 'en échec')}</span></>}
          </span>
        </button>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${kpi === 'visible' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'visible'}
                title="Cliquer pour ne montrer que les vidéos visibles ajoutées sur 7 jours (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'visible' ? null : 'visible')}>
          <span className={styles.kpiLabel}>Visibles (7 jours)</span>
          <span className={`${styles.kpiValue} ${tabs.kpiValueRow}`}>{st?.ready ?? '–'}{st && <small>/ {stats!.week}</small>}</span>
          {st && <>
            <span className={tabs.split} aria-hidden="true">
              <span className={tabs.cReady}  style={{ width: `${st.ready / total * 100}%` }}/>
              <span className={tabs.cHidden} style={{ width: `${st.hidden / total * 100}%` }}/>
              <span className={tabs.cWork}   style={{ width: `${st.transcoding / total * 100}%` }}/>
              <span className={tabs.cFail}   style={{ width: `${st.failed / total * 100}%` }}/>
            </span>
            <span className={styles.originLegend}>
              <span><i className={tabs.cReady}/>Visibles {st.ready}</span>
              <span><i className={tabs.cHidden}/>Masquées {st.hidden}</span>
              {st.transcoding > 0 && <span><i className={tabs.cWork}/>En cours {st.transcoding}</span>}
              {st.failed > 0 && <span><i className={tabs.cFail}/>Échec {st.failed}</span>}
            </span>
          </>}
        </button>
        <RecentCommentsKpi adminId={adminId} open={commentsOpen} onToggle={() => setCommentsOpen(o => !o)} />
      </div>

      {commentsOpen && <CommentsPanel adminId={adminId}
                         onPlay={(name, publicRef) => onPlay({ name, publicRef, state: 'ready' } as DashboardVideo)} />}

      {!commentsOpen && overview}

      {/* ── Filtres de l'onglet ── */}
      {!commentsOpen && <>
      <div className={styles.toolbar}>
        <div className={extra.search}>
          <svg className={extra.searchIcon} width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="7" cy="7" r="5"/><path d="M11 11l4 4"/></svg>
          <input className={extra.searchInput} type="text" value={query} placeholder="Titre, créateur…" aria-label="Rechercher une vidéo"
                 onChange={e => setQuery(e.target.value)} />
          {query && <button type="button" className={extra.searchClear} onClick={() => setQuery('')} aria-label="Effacer la recherche">✕</button>}
        </div>
        <Segment label="État" value={state} onChange={setState}
                 choices={[ ['all', `Toutes ${counts.all}`], ['transcoding', `En transcodage ${counts.transcoding}`], ['ready', `Visibles ${counts.ready}`],
                            ['hidden', `Masquées ${counts.hidden}`], ['failed', `En échec ${counts.failed}`] ] as const} />
        {universes.length > 1 &&
          <Segment label="Univers" value={universe} onChange={setUniverse}
                   choices={[ ['all', 'Tous les univers'] as const, ...universes.map(u => [u, u] as const) ]} />}
        {localFilters && <>
          <span className={extra.count} aria-live="polite"><strong>{plural(shown.length, 'vidéo')}</strong> sur {videos.length}</span>
          <button type="button" className={extra.reset} onClick={resetLocal}>Réinitialiser</button>
        </>}
        <span className={styles.spacer}/>
        <label className={extra.sort}>Trier par
          <select value={sort} onChange={e => setSort(e.target.value as Sort)}>
            {SORTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {error && <div className={styles.empty}>Chargement impossible.</div>}

      {!error && shown.length === 0 &&
        <EmptyState loading={loading} title="Aucune vidéo sur cette période"
                    hint="Élargissez la période ou retirez un filtre. Les nouvelles vidéos apparaissent ici d'elles-mêmes, relevées chaque minute."
                    canClear={filtered || localFilters}
                    onClear={() => { resetLocal(); onClearFilters(); }} />}

      {/* ── Vue cartes ── */}
      {!error && view === 'cards' && shown.length > 0 && (
        <>
          {groups.map(g => (
            <section key={g.title} className={styles.day}>
              <h3 className={styles.dayTitle}>{g.title} <span className={styles.muted}>{plural(g.items.length, 'vidéo')}</span></h3>
              <div className={styles.cards}>
                {g.items.map(v => (
                  <article key={v.id} {...mark(v)} className={`${styles.card} ${isNew(v) ? styles.cardNew : ''} ${v.state === 'failed' ? tabs.cardWarn : ''}`}>
                    <Thumb v={v} onPlay={onPlay} />
                    <div className={styles.cardBody}>
                      <span className={styles.cardName} title={v.name}>{v.name}</span>
                      <Owner owner={v.owner} email={v.ownerEmail} small />
                      <Meta v={v} />
                      <div className={styles.chips}>
                        {v.universe && <span>{v.universe}</span>}
                        <span>{ORIGIN_LABEL[v.origin]}</span>
                        {creatorPlace(v.geo) && <span title={CREATOR_PLACE_HINT}>📍 {creatorPlace(v.geo)}</span>}
                        {v.social.length > 0 && <span className={tabs.chipHl}>{v.state === 'transcoding' ? 'Vers' : 'Pour'} {v.social.map(socialText).join(', ')}</span>}
                      </div>
                      <Actions v={v} onPlay={onPlay} onSheet={onSheet} onRetry={onRetry} onToggle={canModerate ? askToggle : undefined} feature={canModerate && adminId && onFeatureChanged ? { adminId, onChanged: onFeatureChanged } : undefined} />
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </>
      )}

      {toggling && <AvailabilityDialog video={toggling} adminId={adminId} onClose={() => setToggling(null)} onConfirm={onAvailability} />}

      {/* ── Vue liste ── */}
      {!error && view === 'list' && shown.length > 0 && (
        <ListTable items={shown} rowKey={v => v.id} rowProps={v => mark(v, true)} dateOf={v => v.creationDate}
                   noun={{ one: 'vidéo', many: 'vidéos' }}
                   sort={sort === 'recent' ? null : sort} onSort={id => setSort((id ?? 'recent') as Sort)}
                   columns={[
          { id: 'video', label: 'Vidéo', cell: v =>
              <TitleCell thumb={<MiniThumb v={v} />} title={v.name} titleHint={v.name}
                         sub={[ v.universe, ORIGIN_LABEL[v.origin], resolutionsText(v.resolutions) ].filter(Boolean).join(' · ')} /> },
          { id: 'state', label: 'État', cell: v => <StateBadge v={v} /> },
          { id: 'creator', label: 'Créateur', cell: v => <CreatorCell owner={v.owner} email={v.ownerEmail} geo={v.geo} /> },
          { id: 'duration', label: 'Durée', right: true, sort: COMPARE.duration,
            cell: v => v.state === 'failed' || !clock(v.durationSeconds) ? <Dash/> : clock(v.durationSeconds) },
          { id: 'views', label: 'Vues', right: true, sort: COMPARE.views,
            cell: v => v.state === 'ready' || v.state === 'hidden' ? v.views.toLocaleString('fr-FR') : <Dash/> },
          { id: 'size', label: 'Taille', right: true, hide: 'md', sort: COMPARE.size, cell: v => size(v.sizeBytes) || <Dash/> },
          { id: 'date', label: 'Ajoutée', cell: v => <WhenCell iso={v.creationDate} /> },
          { id: 'actions', label: '', right: true, cell: v =>
              <div className={extra.always}><RowActions><Actions v={v} onPlay={onPlay} onSheet={onSheet} onRetry={onRetry} onToggle={canModerate ? askToggle : undefined} feature={canModerate && adminId && onFeatureChanged ? { adminId, onChanged: onFeatureChanged } : undefined} playIcon /></RowActions></div> },
        ]} />
      )}
      </>}
    </div>
  );
};

export default VideosSection;
